import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@repo/blacksmith-ui/dialog";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { Suspense, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { cn } from "@repo/blacksmith-ui/cn";
import type { AxiosError } from "axios";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { useAtom } from "jotai";
import { migrationWizardOpenAtom, migrationWizardStepAtom } from "./migrationWizardState";
import { useActiveGithubOrgForUser, useIsPersonalOrg } from "@/hooks/useConnectedGithubOrgs";
import { BSErrorBoundary } from "./ui/bs-error-boundary";
import { usePostHog } from "posthog-js/react";
import { MIGRATION_WIZARD_EVENTS } from "@/lib/posthog-constants";
import { useOpenMigrationWizard } from "./useOpenMigrationWizard";
import { searchRepositories } from "@/lib/repositories";
import { useApi } from "@/hooks/useApi";
import { useQueryClient } from "react-query";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import Image from "next/image";
import MigrationWizardSvg from "@/assets/migration-wizard.svg";
import { useSidebar } from "@repo/blacksmith-ui/sidebar";
import { orgApiPath } from "@/lib/named-org";
function MigrationWizardSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col h-[300px] w-full gap-4"><Skeleton className="h-8 w-48 rounded-lg" /><Skeleton className="h-6 w-96 mb-4 rounded-lg" /><Skeleton className="w-full h-32 rounded-lg" /><Skeleton className="w-full h-12 rounded-lg" /><Skeleton className="w-full h-12 rounded-lg" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// DialogContent only mounts children while open, so the body chunk loads on first open.
const MigrationWizard = dynamic(() => import("./MigrationWizard").then(m => m.MigrationWizard), {
  loading: MigrationWizardSkeleton
});
function ErrorFallback(t0) {
  const $ = _c(2);
  const {
    error
  } = t0;
  if (error?.response?.status === 403) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DialogHeader><DialogTitle /><DialogDescription /></DialogHeader>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <>{t1}<div><div className="flex items-center justify-center h-[150px] w-full"><ExclamationCircleIcon className="w-12 h-12 text-red-500" /></div></div></>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  throw error;
}

/**
 * Sidebar trigger for the Migration Wizard. It only flips
 * `migrationWizardOpenAtom`; the dialog itself is `MigrationWizardDialog`,
 * mounted once in SidebarLayout so every route can open it.
 */
export default function LazyMigrationWizard() {
  const openMigrationWizard = useOpenMigrationWizard("sidebar");
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUser();
  const {
    isPersonalOrg
  } = useIsPersonalOrg();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  return <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button type="button" aria-haspopup="dialog" aria-disabled={isPersonalOrg} className={cn(
      // Match the sidebar navigation item styling (not a "card").
      "group/migwiz mb-0 flex h-8 w-full items-center gap-2 rounded-sm p-1.5 text-left type-body-s-regular", "text-[#434546] hover:bg-[#0000000f] hover:text-[#161718]", "dark:text-[#bbb] dark:hover:bg-white/6 dark:hover:text-white",
      // Standard focus styles per design.md - only show border on focus-visible, not on regular focus
      "outline-hidden border border-transparent focus-visible:!border-2 focus-visible:!border-[var(--focus-border)] focus-visible:ring-0", "group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[state=collapsed]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8", isPersonalOrg ? "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-[#434546] dark:hover:bg-transparent dark:hover:text-[#bbb]" : "cursor-pointer")} onClick={() => {
        if (isPersonalOrg) return;
        openMigrationWizard();
      }} onMouseEnter={() => {
        if (isPersonalOrg) return;
        void import("./MigrationWizard");
        // Eagerly fetch the initial page of repositories for the active org
        // to speed up user interactions with the dropdown in MigrationWizard.
        // Cache key must match the one used by `useRepositorySearch`.
        if (activeNamedOrg) {
          queryClient.prefetchQuery(["repositorySearch", activeNamedOrg, "", 100], () => searchRepositories({
            api,
            endpoint: `${orgApiPath(activeNamedOrg)}/migrate-runners/repositories`,
            search: "",
            perPage: 100
          }), {
            staleTime: 30_000
          });
        }
      }}>
          <span className="flex size-[18px] shrink-0 items-center justify-center">
            <Image src={MigrationWizardSvg} alt="" aria-hidden="true" width={16} height={16} className="size-4" />
          </span>
          {/* whitespace-nowrap keeps the label on one line while the
              sidebar width is still animating open. */}
          <span className="whitespace-nowrap group-data-[state=collapsed]:hidden">
            Migration Wizard
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={sidebarState !== "collapsed" || isMobile}>
        {isPersonalOrg ? "Blacksmith doesn't support personal orgs" : "Migration Wizard"}
      </TooltipContent>
    </Tooltip>;
}
export function MigrationWizardDialog() {
  const $ = _c(21);
  const [open, setOpen] = useAtom(migrationWizardOpenAtom);
  const [step, setStep] = useAtom(migrationWizardStepAtom);
  const posthog = usePostHog();
  const returnFocusRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== open || $[1] !== setStep) {
    t0 = () => {
      if (open) {
        setStep("repository");
      }
    };
    t1 = [open, setStep];
    $[0] = open;
    $[1] = setStep;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  const canClose = step === "repository" || step === "pull-request";
  let t2;
  if ($[4] !== posthog || $[5] !== setOpen || $[6] !== step) {
    t2 = next => {
      if (!next) {
        posthog?.capture(MIGRATION_WIZARD_EVENTS.CLOSE, {
          step
        });
      }
      setOpen(next);
    };
    $[4] = posthog;
    $[5] = setOpen;
    $[6] = step;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    };
    t4 = e => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      if (target?.isConnected) {
        e.preventDefault();
        target.focus();
      }
    };
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] !== canClose) {
    t5 = e_0 => {
      const target_0 = e_0.detail.originalEvent.target as Element | null;
      if (target_0?.closest("[data-radix-popper-content-wrapper]")) {
        e_0.preventDefault();
        return;
      }
      if (!canClose) {
        e_0.preventDefault();
      }
    };
    t6 = e_1 => {
      if (!canClose) {
        e_1.preventDefault();
      }
    };
    $[10] = canClose;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <BSErrorBoundary title="Migration Wizard" FallbackComponent={ErrorFallback}><Suspense fallback={<MigrationWizardSkeleton />}><MigrationWizard /></Suspense></BSErrorBoundary>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6) {
    t8 = <DialogContent translate="no" className="notranslate w-[760px] max-w-[760px] max-h-[92vh] overflow-y-auto" onOpenAutoFocus={t3} onCloseAutoFocus={t4} onInteractOutside={t5} onEscapeKeyDown={t6}>{t7}</DialogContent>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== open || $[18] !== t2 || $[19] !== t8) {
    t9 = <Dialog open={open} onOpenChange={t2}>{t8}</Dialog>;
    $[17] = open;
    $[18] = t2;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}